import classNames from 'classnames';
import React, { forwardRef, useEffect, useId, useState } from 'react';
import InlineError from '../InlineError';
import NumberInput from '../NumberInput';
import { TextInputProps } from '../TextInput';
import Tooltip from '../Tooltip';
import './SortCodeInput.scss';

interface OnBlurValue {
  pair1?: string;
  pair2?: string;
  pair3?: string;
  sortCode?: string;
}

export interface SortCodeInputProps extends Omit<TextInputProps, 'onBlur'> {
  onBlur: (value: OnBlurValue) => void;
}

const SortCodeInput = forwardRef<HTMLInputElement, SortCodeInputProps>(
  (
    {
      label,
      hideLabel,
      required,
      error,
      disabled = false,
      pii = false,
      tooltip,
      tooltipAriaLabel,
      value,
      onBlur
    },
    ref
  ) => {
    const id = useId();

    const [pair1, setPair1] = useState(value ? `${value}`.slice(0, 2) : null);
    const [pair2, setPair2] = useState(value ? `${value}`.slice(2, 4) : null);
    const [pair3, setPair3] = useState(value ? `${value}`.slice(4) : null);

    const onInputBlur = ({
      pair1: pair1Val,
      pair2: pair2Val,
      pair3: pair3Val
    }: {
      pair1: string;
      pair2: string;
      pair3: string;
    }) => {
      const pair1DisplayValue = pair1Val;
      const pair2DisplayValue = pair2Val;
      const pair3DisplayValue = pair3Val;

      const sortCode = `${pair1DisplayValue}${pair2DisplayValue}${pair3DisplayValue}`;

      const isAllPartsEmpty = [pair1Val, pair2Val, pair3Val].every((val) => val === '');
      const isSomePartsEmpty = [pair1Val, pair2Val, pair3Val].some((val) => val === '');
      const isSomePartsCompleted = [pair1Val, pair2Val, pair3Val].some((val) => val?.length);

      const sortCodeFallback =
        isAllPartsEmpty || (isSomePartsEmpty && isSomePartsCompleted) ? '' : null;

      const isValidSortCode = new RegExp('^\\d{6}$').test(sortCode);
      return onBlur({
        pair1: pair1Val,
        pair2: pair2Val,
        pair3: pair3Val,
        sortCode: isValidSortCode ? sortCode : sortCodeFallback
      });
    };

    const focusNextInput = (evt: React.ChangeEvent<HTMLInputElement>) => {
      const { value, name, maxLength } = evt.target;
      if (value.length >= maxLength) {
        const nextFieldName = name === 'pair1' ? 'pair2' : 'pair3';
        const nextField = document.querySelector<HTMLElement>(`input[name=${nextFieldName}]`);
        if (nextField !== null) {
          nextField.focus();
        }
      }
    };

    /**
     * When value is changed we need to update the state to reflect that
     */
    useEffect(() => {
      setPair1(value ? `${value}`.slice(0, 2) : null);
      setPair2(value ? `${value}`.slice(2, 4) : null);
      setPair3(value ? `${value}`.slice(4) : null);
    }, [value]);

    return (
      <fieldset
        className={classNames('mss-form-control mss-sort-code-input', {
          'ctHidden pii-data': pii
        })}
        aria-describedby={error ? `error-${id}` : undefined}
        aria-labelledby={`sort-code-label-${id}`}
        aria-invalid={!!error}
      >
        <div
          id={`sort-code-label-${id}`}
          className={classNames('mss-form-control-label', {
            'sr-only': hideLabel
          })}
        >
          {label}
          {required && (
            <span className="mss-form-control-required" aria-hidden>
              *
            </span>
          )}
          {tooltip && (
            <Tooltip content={tooltip} buttonProps={{ 'aria-label': tooltipAriaLabel }} />
          )}
        </div>
        <div>
          <NumberInput
            label="Pair 1"
            name="pair1"
            error={!!error}
            ref={ref}
            required={required}
            value={pair1 || ''}
            onBlur={(evt) => {
              setPair1(evt.target.value);
              onInputBlur({ pair1: evt.target.value, pair2, pair3 });
            }}
            onChange={(evt) => {
              setPair1(evt.target.value);
              focusNextInput(evt);
            }}
            maxLength={2}
            inputProps={{
              'aria-describedby': error ? `error-${id}` : undefined
            }}
            disabled={disabled}
            hideLabel
          />
          <NumberInput
            label="Pair 2"
            name="pair2"
            error={!!error}
            required={required}
            value={pair2 || ''}
            onBlur={(evt) => {
              setPair2(evt.target.value);
              onInputBlur({ pair1, pair2: evt.target.value, pair3 });
            }}
            onChange={(evt) => {
              setPair2(evt.target.value);
              focusNextInput(evt);
            }}
            maxLength={2}
            inputProps={{
              'aria-describedby': error ? `error-${id}` : undefined
            }}
            disabled={disabled}
            hideLabel
          />
          <NumberInput
            label="Pair 3"
            name="pair3"
            error={!!error}
            required={required}
            value={pair3 || ''}
            onBlur={(evt) => {
              setPair3(evt.target.value);
              onInputBlur({ pair1, pair2, pair3: evt.target.value });
            }}
            onChange={(evt) => setPair3(evt.target.value)}
            maxLength={2}
            inputProps={{
              'aria-describedby': error ? `error-${id}` : undefined
            }}
            disabled={disabled}
            hideLabel
          />
        </div>
        <span role="alert">
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          {((error as any) instanceof String || typeof error === 'string') && (
            <InlineError error={error as string} id={id} />
          )}
        </span>
      </fieldset>
    );
  }
);

export default SortCodeInput;
